/**
 * WaveformIcon — the tile artwork for a preset. Design direction #4
 * from the picker ("Waveform fingerprint on brand gradient"): each
 * preset is a 10-bar waveform in mixgenie's pink→purple gradient, with
 * the bar heights seeded from the preset itself so every tile is
 * visibly unique AND stable across renders.
 *
 * Why generate rather than hand-draw: 30+ presets today and a growing
 * catalog. Hand-tuning each one blocks ever adding a preset. We instead
 * (a) hash the preset's own id/name into a deterministic bar pattern
 * and (b) bias that pattern per category so "electronic" feels dense +
 * even, "jazz" feels syncopated, "cinematic" feels sparse + swelling.
 * A new preset drops in with a correct-feeling icon the moment it ships.
 */
import { useMemo } from 'react';

type ShapeBias = {
  // Density range (how many of the 10 bars are "live" vs near-zero).
  density: [number, number];
  // Base amplitude range (0..24) — the floor and ceiling for live bars.
  amp: [number, number];
  // Peakiness — probability a live bar spikes to the ceiling.
  peakiness: number;
  // Smoothness — 1 = neighbours track each other (sustained),
  // 0 = independent (percussive).
  smoothness: number;
  // Optional per-position tilt (e.g. cinematic swells low → high).
  tilt?: 'rise' | 'fall' | 'arch' | 'valley';
};

/**
 * Category biases. Keys are matched case-insensitively against the
 * preset's `category` field. Unknown categories fall back to DEFAULT.
 * Numbers tuned by eye against the picker artifact — if you edit them,
 * reload the composer and sanity-check the Presets sheet.
 */
const BIASES: Record<string, ShapeBias> = {
  // Four-on-the-floor, consistent levels, periodic peaks.
  electronic: { density: [0.9, 1.0], amp: [10, 22], peakiness: 0.35, smoothness: 0.6 },
  house:      { density: [0.95, 1.0], amp: [12, 22], peakiness: 0.3,  smoothness: 0.7 },
  techno:     { density: [1.0, 1.0], amp: [14, 24], peakiness: 0.4,  smoothness: 0.5 },

  // Big punches, big gaps, hard transients.
  trap:       { density: [0.6, 0.85], amp: [4, 24], peakiness: 0.7, smoothness: 0.15 },
  'hip-hop':  { density: [0.65, 0.9], amp: [6, 22], peakiness: 0.5, smoothness: 0.25 },
  rap:        { density: [0.65, 0.9], amp: [6, 22], peakiness: 0.5, smoothness: 0.25 },
  drill:      { density: [0.7, 0.95], amp: [6, 24], peakiness: 0.65, smoothness: 0.2 },

  // Chill + warm — low ceiling, high density, very smooth.
  'lo-fi':    { density: [0.9, 1.0], amp: [4, 14], peakiness: 0.1,  smoothness: 0.85 },
  ambient:    { density: [0.8, 1.0], amp: [3, 12], peakiness: 0.05, smoothness: 0.95, tilt: 'arch' },
  chill:      { density: [0.85, 1.0], amp: [5, 15], peakiness: 0.1, smoothness: 0.9 },

  // Vocal-driven, melodic swells.
  pop:        { density: [0.75, 0.95], amp: [8, 22], peakiness: 0.3, smoothness: 0.75 },
  acoustic:   { density: [0.75, 0.95], amp: [6, 18], peakiness: 0.2, smoothness: 0.8 },
  rnb:        { density: [0.8, 1.0], amp: [8, 20], peakiness: 0.25, smoothness: 0.8 },
  soul:       { density: [0.8, 1.0], amp: [8, 20], peakiness: 0.3, smoothness: 0.75 },

  // Irregular meter, strong peaks, broken rhythms.
  jazz:       { density: [0.6, 0.95], amp: [4, 24], peakiness: 0.55, smoothness: 0.3 },
  funk:       { density: [0.85, 1.0], amp: [6, 24], peakiness: 0.5, smoothness: 0.4 },
  blues:      { density: [0.7, 0.9], amp: [6, 20], peakiness: 0.35, smoothness: 0.5 },

  // Guitars, drums, loud.
  rock:       { density: [0.9, 1.0], amp: [10, 24], peakiness: 0.5, smoothness: 0.4 },
  metal:      { density: [1.0, 1.0], amp: [14, 24], peakiness: 0.65, smoothness: 0.3 },
  punk:       { density: [0.95, 1.0], amp: [12, 24], peakiness: 0.6, smoothness: 0.35 },
  indie:      { density: [0.8, 1.0], amp: [8, 22], peakiness: 0.35, smoothness: 0.5 },

  // Classical / orchestral — long dynamic arcs.
  classical:  { density: [0.7, 1.0], amp: [4, 24], peakiness: 0.3, smoothness: 0.9, tilt: 'arch' },
  orchestral: { density: [0.75, 1.0], amp: [4, 24], peakiness: 0.35, smoothness: 0.9, tilt: 'arch' },
  cinematic:  { density: [0.7, 1.0], amp: [3, 24], peakiness: 0.4, smoothness: 0.9, tilt: 'arch' },
  score:      { density: [0.7, 1.0], amp: [3, 24], peakiness: 0.4, smoothness: 0.9, tilt: 'rise' },

  // World / Latin / Folk — periodic phrasing.
  latin:      { density: [0.85, 1.0], amp: [8, 22], peakiness: 0.4, smoothness: 0.6 },
  reggaeton:  { density: [0.9, 1.0], amp: [10, 22], peakiness: 0.5, smoothness: 0.5 },
  reggae:     { density: [0.75, 0.95], amp: [6, 18], peakiness: 0.3, smoothness: 0.7 },
  afrobeats:  { density: [0.85, 1.0], amp: [8, 22], peakiness: 0.45, smoothness: 0.55 },
  folk:       { density: [0.75, 0.95], amp: [5, 18], peakiness: 0.2, smoothness: 0.75 },
  country:    { density: [0.8, 1.0], amp: [6, 20], peakiness: 0.3, smoothness: 0.65 },
  world:      { density: [0.8, 1.0], amp: [6, 22], peakiness: 0.35, smoothness: 0.6 },
};
const DEFAULT_BIAS: ShapeBias = { density: [0.8, 1.0], amp: [6, 22], peakiness: 0.35, smoothness: 0.6 };

const BAR_COUNT = 10;
const MIN_HEIGHT_PX = 2;
const MAX_HEIGHT_PX = 24;

// -- deterministic pseudo-random generator -----------------------------------
// Simple FNV-1a → mulberry32. Takes any string, returns a function that
// yields the same sequence every time. Keeps icons stable across renders
// and across users on the same build.
function hash32(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
function mulberry32(seed: number) {
  let a = seed;
  return () => {
    a = (a + 0x6D2B79F5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function tiltMultiplier(i: number, kind?: 'rise' | 'fall' | 'arch' | 'valley'): number {
  const t = i / (BAR_COUNT - 1); // 0..1
  switch (kind) {
    case 'rise':   return 0.35 + 0.65 * t;
    case 'fall':   return 1.0 - 0.65 * t;
    case 'arch':   return 0.35 + Math.sin(t * Math.PI) * 0.65;
    case 'valley': return 1.0 - Math.sin(t * Math.PI) * 0.5;
    default:       return 1;
  }
}

/**
 * Generate 10 bar heights (in SVG units, 0..MAX_HEIGHT_PX) seeded by
 * the preset id + name, biased by the preset category.
 */
export function generateWaveformPattern(seedKey: string, category?: string): number[] {
  const bias = (category && BIASES[category.toLowerCase().trim()]) || DEFAULT_BIAS;
  const rng = mulberry32(hash32(seedKey || 'preset'));

  const densityRoll = bias.density[0] + rng() * (bias.density[1] - bias.density[0]);
  const [ampLo, ampHi] = bias.amp;

  const heights: number[] = [];
  let prev = ampLo + rng() * (ampHi - ampLo);

  for (let i = 0; i < BAR_COUNT; i++) {
    const alive = rng() < densityRoll;
    if (!alive) { heights.push(MIN_HEIGHT_PX); continue; }

    const target = rng() < bias.peakiness
      ? ampHi
      : ampLo + rng() * (ampHi - ampLo);
    // Smoothness blends between prev value and target.
    const v = prev * bias.smoothness + target * (1 - bias.smoothness);
    const tilt = tiltMultiplier(i, bias.tilt);
    const biased = Math.max(MIN_HEIGHT_PX, Math.min(MAX_HEIGHT_PX, v * tilt));
    heights.push(biased);
    prev = biased;
  }

  // Guarantee at least ONE visible peak so a lo-fi / ambient preset
  // doesn't render as a near-flat line that reads as "broken icon".
  const maxH = Math.max(...heights);
  if (maxH < ampHi * 0.6) {
    const peakIdx = Math.floor(rng() * BAR_COUNT);
    heights[peakIdx] = Math.max(heights[peakIdx], ampHi * 0.75);
  }
  return heights;
}

/**
 * WaveformIcon — fits-content SVG, bars rendered in the brand pink→
 * purple gradient. Pass an explicit `pattern` to hand-tune a preset, or
 * let the hook derive one from `seedKey` + `category`.
 */
export function WaveformIcon({
  seedKey,
  category,
  pattern,
  size = 44,
  gradientId,
  bg = 'brand',
}: {
  seedKey: string;
  category?: string;
  pattern?: number[];
  size?: number;
  // When embedding multiple WaveformIcons in one page, each one needs a
  // unique gradient id or Safari re-uses the first paint for all of
  // them. Caller passes a stable id (preset _id is perfect). Falls back
  // to a seed-derived id if not provided.
  gradientId?: string;
  // 'brand' = full brand gradient on surfaceHi tile (default).
  // 'onBrand' = white bars for use on top of an already-gradient tile.
  bg?: 'brand' | 'onBrand';
}) {
  const heights = useMemo(() => pattern ?? generateWaveformPattern(seedKey, category), [pattern, seedKey, category]);
  const gid = gradientId || `wave-${hash32(seedKey)}`;

  const vbW = BAR_COUNT * 4; // 2px bar + 2px gap
  const vbH = MAX_HEIGHT_PX + 2;

  return (
    <svg
      viewBox={`0 0 ${vbW} ${vbH}`}
      width={size}
      height={Math.round(size * (vbH / vbW))}
      role="img"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      {bg === 'brand' && (
        <defs>
          <linearGradient id={gid} x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" stopColor="#E94A8F" />
            <stop offset="1" stopColor="#7354FF" />
          </linearGradient>
        </defs>
      )}
      {heights.map((h, i) => (
        <rect
          key={i}
          x={i * 4}
          y={(vbH - h) / 2}
          width={2}
          height={h}
          rx={1}
          fill={bg === 'brand' ? `url(#${gid})` : '#ffffff'}
        />
      ))}
    </svg>
  );
}
